import Utils from "@/common/utils/utils";
import { I18n } from "aws-amplify";
import { useState, useEffect, useRef } from "react";
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import {Popover, Box, Tooltip} from '@material-ui/core';
import Checkbox from "@material-ui/core/Checkbox";

const EmailMultiChoiceInput = ({
    addressList,
    setAddressList,
    setIsEdited,
    listSuggestSearchEmail,
    disable = false,
    required = false,
    triggerClearError = false
}) => {
    const [searchAddress, setSearchAddress] = useState('')
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [showListSuggestion, setShowListSuggestion] = useState(true);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [errMsg, setErrMsg] = useState('');

    const inputRef = useRef(null)
    const listSuggestRef = useRef(null)
    const suggestionRefs = useRef([]);

    const pushAddress = (suggestList, value) => {
        const foundMail = suggestList.find(suggest => suggest.mail === value)
        if (foundMail) {
            setAddressList(prev => [...prev, foundMail])
        } else {
            const newUserMail = {id: `new_${Date.now()}`, mail: value, name: ''}
            setAddressList(prev => [...prev, newUserMail])
        }
        setIsEdited(true)
        setSearchAddress('')
        setFilteredSuggestions([])
    }
    
    const handleSearchAddress = (e) => {
        let searchValue = e.target.value
        if (Utils.isEmptyString(searchValue)) {
            setSearchAddress('')
            setFilteredSuggestions([])
            return
        };

        // handle match keyword in listSuggestSearchEmail
        const filtered = [...listSuggestSearchEmail].filter((suggestion) => {
            if (suggestion.userId) {
                return (
                    suggestion.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                    suggestion.mail.toLowerCase().includes(searchValue.toLowerCase())
                );
            } else {
                return (
                    suggestion?.orgName?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    suggestion?.orgDomain?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    suggestion?.name?.toLowerCase().includes(searchValue.toLowerCase())
                );
            }
        });

        setFilteredSuggestions([...filtered]);

        setSearchAddress(searchValue)
        if (searchValue.endsWith(',')) {
            searchValue = searchValue.slice(0, -1);
            const isEmail = Utils.validateEmailVer2(searchValue)
            if (isEmail) {
                searchValue = searchValue.toLowerCase()
                pushAddress(listSuggestSearchEmail, searchValue)
            }
        }
    }

    const handleBlurSearchAddress = (e) => {
        let searchValue = e.target.value
        // if (searchValue.length > 200) {
        //     setIsEdited(false)
        // }

        const isEmail = Utils.validateEmailVer2(searchValue)
        if (isEmail) {
            searchValue = searchValue.toLowerCase()
            pushAddress(listSuggestSearchEmail, searchValue)
            setErrMsg('');
            return;
        }

        // when clicked listSuggest
        if (listSuggestRef?.current) return;
        if (required && Utils.isEmptyArray(addressList)) {
            setErrMsg(I18n.get('MSG-CM-REQUIRED-INPUT'));
        } else {
            setErrMsg('');
        }
    }

    const handleKeyDownSearchAddress = (e) => {
        if (e.key === "ArrowDown") {
            setActiveIndex((prevIndex) =>
                prevIndex < filteredSuggestions.length - 1 ? prevIndex + 1 : prevIndex
            );
        } else if (e.key === "ArrowUp") {
            setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
        } else if (e.key === "Enter" && activeIndex >= 0) {
            const selectedSuggest = filteredSuggestions[activeIndex];
            handleSelectAddress(selectedSuggest)
            setActiveIndex(-1)
        } else if (e.key === 'Enter' && activeIndex < 0) {
            let searchValue = e.target.value.trim();
            if (Utils.isEmptyString(searchValue)) return;
            const isEmail = Utils.validateEmailVer2(searchValue)
            if (isEmail) {
                searchValue = searchValue.toLowerCase()
                pushAddress(listSuggestSearchEmail, searchValue)
            }
        } else if (e.key === 'Backspace') {
            let searchValue = e.target.value.trim()
            if (Utils.isEmptyString(searchValue) && !Utils.isEmptyArray(addressList)) {
                // Remove the last address from the list
                const updatedAddressList = addressList.slice(0, -1);
                setAddressList(updatedAddressList);
                setIsEdited(true)
            }
        }
        setErrMsg('');
    }

    const handleRemoveAddress = (removeAddress) => {
        if (disable) return;
        setAddressList(prev => [...prev].filter(address => address.id !== removeAddress.id))
        setIsEdited(true)
        if (required) {
            let newAddressList = addressList.filter(address => address.id !== removeAddress.id);
            if (Utils.isEmptyArray(newAddressList)) {
                setErrMsg(I18n.get('MSG-CM-REQUIRED-INPUT'));
                return;
            }
            if (newAddressList.length === 1) {
                const isUncheckedAll = !!newAddressList[0].projectId && newAddressList[0].userList.every(item => !item.selected);
                if (isUncheckedAll) {
                    setErrMsg(I18n.get('MSG-CM-REQUIRED-INPUT'));
                    return;
                }
            }
        }
    }

    const handleSelectAddress = (suggest) => {
        const existItem = addressList.find(address => address.id === suggest.id)
        if (!existItem) {
            const isSelectedProject = suggest.projectId && !Utils.isEmptyString(suggest.projectId)
            if (isSelectedProject) {
                let userList = [...suggest.userList].map(user => ({
                    ...user,
                    selected: true
                }))
                setAddressList(prev => [...prev, {
                    ...suggest,
                    userList: userList
                }])
            } else {
                setAddressList(prev => [...prev, suggest])
            }
            setSearchAddress('')
            setFilteredSuggestions([])
            setErrMsg('');
            setIsEdited(true)
        }
    }
    
    // handle show/hidden suggest list
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the clicked element is not inside the suggestion list or the search input
            if (!inputRef?.current || !listSuggestRef?.current) return;
            if (!inputRef.current.contains(event.target) && !listSuggestRef.current.contains(event.target)) {
                setShowListSuggestion(false);
            }
        };

        if (!showListSuggestion) return;
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showListSuggestion]);

    // handle scroll suggest list when navigate up/down key
    useEffect(() => {
        if (activeIndex === -1) return;
        if (suggestionRefs.current[activeIndex]) {
            suggestionRefs.current[activeIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [activeIndex]);

    useEffect(() => {
        if (triggerClearError) {
            setErrMsg('')
            setSearchAddress('')
            setShowListSuggestion([])
            setActiveIndex(-1)
        }
    }, [triggerClearError])


    return (
        <div className="email-response-address-multi-choice-wrap">
            <div
                className={`email-response-address-multi-choice${disable ? ' disable' : ''} ${errMsg ? ' error': ''}`}
            >
                {!Utils.isEmptyArray(addressList) && (
                    addressList.map((address, index) => {
                        return (
                            <EmailChip
                                chip={address}
                                disabled={disable}
                                handleRemoveAddress={handleRemoveAddress}
                                addressList={addressList}
                                setAddressList={setAddressList}
                                setIsEdited={setIsEdited}
                                setErrMsg={setErrMsg}
                                required={required}
                            />
                        )
                    })
                )}
                <input
                    className={`email-response-address-multi-choice-input ${disable ? ' disable' : ''}`}
                    ref={inputRef}
                    disabled={disable}
                    value={searchAddress}
                    onChange={e => handleSearchAddress(e)}
                    onBlur={e => handleBlurSearchAddress(e)}
                    onKeyDown={e => handleKeyDownSearchAddress(e)}
                    onFocus={e => setShowListSuggestion(true)}
                />
            </div>
            {!Utils.isEmptyString(errMsg) && <p className="email-signature-error-text">{errMsg}</p>}

            {/* Suggestion-list */}
            {(showListSuggestion && !Utils.isEmptyArray(filteredSuggestions)) && (
                <div className="address-suggestion-list-wrap" ref={listSuggestRef}>
                    <div className="address-suggestion-list-inner">
                        {filteredSuggestions.map((suggest, index) => (
                            <div
                                ref={el => suggestionRefs.current[index] = el}
                                key={suggest.id}
                                className={`
                                    address-suggest-item
                                    ${addressList.some(item => item.id === suggest.id) ? ' selected': ''}
                                    ${activeIndex === index ? ' navigate-up-down': ''}
                                `}
                            >
                                <OverFlowTooltipWithTabsMui
                                    className="address-suggest-title"
                                    value={suggest.userId
                                        ? !Utils.isEmptyString(suggest.name)
                                            ? `${suggest.name} (${suggest.mail})`
                                            : `${suggest.mail}`
                                        : `${suggest.name}`
                                    }
                                    onClick={() => handleSelectAddress(suggest)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default EmailMultiChoiceInput;

const EmailChip = ({chip, disabled, handleRemoveAddress, addressList, setAddressList, setIsEdited, setErrMsg, required}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const idChip = open ? 'simple-popover' : undefined;

    let chipName = chip.name
    if (chipName) {
        chipName = chipName.trim()
    }

    return (
        <div key={chip.id}>
            <Box
                className={`email-address-chip ${chip.projectId ? ' cursor-pointer': ' cursor-default'}`}
                aria-describedby={idChip}
                variant="contained"
            >
                <span className="email-address-title" onClick={handleClick}>
                    {chip.projectId ? (
                        <OverFlowTooltipWithTabsMui value={chipName}/>
                    ) : (
                        <Tooltip
                            title={chip.mail}
                            placement='bottom-start'
                            arrow
                        >
                            <p className="email-address-content">{chipName || chip.mail}</p>
                        </Tooltip>
                    )}
                </span>
                {!disabled && (
                    <img
                        src='/assets/image/icon-chip-close.svg'
                        alt='icon-close'
                        className="email-address-chip-close"
                        onClick={() => handleRemoveAddress(chip)}
                    />
                )}
            </Box>
            {!Utils.isEmptyArray(chip.userList) && (
                <Popover
                    id={idChip}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                >
                    <div className="project-user-list-popover">
                        {chip?.userList?.map(user => (
                            <div className="user-email-info" key={user.id}>
                                <Checkbox
                                    style={{color: '#2D72D2'}}
                                    size="small"
                                    checked={user.selected}
                                    disabled={disabled}
                                    onChange={e => {
                                        setIsEdited(true)
                                        setErrMsg('')
                                        setAddressList(prev => {
                                            const clonedAddressList = [...prev];
                                            let handleChange = clonedAddressList.find(a => a.id === chip.id);
                                            if (!handleChange) return prev;
                                            let userList = handleChange.userList.map(u => 
                                                u.id === user.id 
                                                    ? { ...u, selected: !u.selected }
                                                    : u
                                            );
                                            const updatedAddressList = clonedAddressList.map(a =>
                                                a.id === chip.id ? { ...a, userList } : a
                                            );
                                    
                                            return updatedAddressList;
                                        });
                                    }}
                                    onBlur={e => {
                                        if (required) {
                                            if (Utils.isEmptyArray(addressList)) {
                                                setErrMsg(I18n.get('MSG-CM-REQUIRED-INPUT'));
                                                return;
                                            }
                                            if (addressList.length === 1) {
                                                const isUncheckedAll = !!addressList[0].projectId && addressList[0].userList.every(item => !item.selected);
                                                if (isUncheckedAll) {
                                                    setErrMsg(I18n.get('MSG-CM-REQUIRED-INPUT'));
                                                    return;
                                                }
                                            }
                                            setErrMsg('');
                                        }
                                    }}
                                />
                                <OverFlowTooltipWithTabsMui className="email-address-title" value={`${user.name} (${Utils.getDisplayUserEmailWithoutSuffix(user.mail)})`}/>
                            </div>
                        ))}
                    </div>
                </Popover>
            )}
        </div>
    )
}
